import React from "react";
import DashboardWrapper from "../../layout/DashboardWrapper/DashboardWrapper";
import ProfileForm from "../../components/ProfileForm/ProfileForm";
import Grid from "@mui/material/Grid";
import styles from "../../styles/components/profileform.module.scss";
import { Box, MenuItem, Select, Stack, Typography } from "@mui/material";
import CustomButton from "../../ui/CustomButton/CustomButton";
import { useMutation, useQueryClient } from "react-query";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import CustomInput from "../../components/CustomInput/CustomInput";
import validationText from "../../json/messages/validationText";
import * as yup from "yup";
import { useNavigate } from "react-router-dom";
import useNotiStack from "../../hooks/useNotistack";
import { administratorList } from "../../json/commonData/jsonListData";
import { adminMemberData, isPasswordMismatch } from "../../services/utils";
import { CreateMCAdminUser } from "../../api/functions/user.api";

const schema = yup.object().shape({
  firstName: yup
    .string()
    .trim()
    .max(30, "length should be within 30 characters.")
    .required(
      <p style={{ fontSize: "12px" }}>{validationText.error.first_name}</p>
    ),
  lastName: yup
    .string()
    .trim()
    .max(30, "length should be within 30 characters.")
    .required()
    .label("Last name"),
  email: yup
    .string()
    .trim()
    .required(
      <p style={{ fontSize: "12px" }}>{validationText.error.enter_email}</p>
    )
    .matches(/^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/, "Email address is not valid"),
  adminLevel: yup.string().required(),
  password: yup
    .string()
    .min(4)
    .max(32)
    .required(validationText.error.enter_password)
    .typeError("Password must be at least 4 characters"),
  confirmPassword: yup.string().required(validationText.error.confirm_password),
});

export default function AddMcAdminUser() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toastSuccess, toastError } = useNotiStack();
  const user = adminMemberData();

  const {
    handleSubmit,
    control,
    watch,
    getValues,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
  });
  const isNotMatchPassword = isPasswordMismatch(watch("password"), watch("confirmPassword"))

  const { mutateAsync: mutateAsyncCreateMCAdminUser, isLoading } = useMutation({
    mutationFn: (payloadData) => CreateMCAdminUser(payloadData, user),
    onSuccess: (response) => {
      if (response.Status === 1) {
        toastSuccess("User created successfully");
        navigate("/dashboard/user-profile");
        queryClient.invalidateQueries(["userdetails"]);
      }
    },
    onError: (res) => {
      toastError("Please try again");
    },
  });

  const onSubmit = (data) => {
    const payloadData = {
      firstName: data?.firstName?.trim(),
      lastName: data?.lastName?.trim(),
      email: data?.email?.trim(),
      administrator: Number(data?.adminLevel),
      password: data?.password?.trim(),
    };

    mutateAsyncCreateMCAdminUser(payloadData);
  };
  const redirectToProfile = () => {
    navigate("/dashboard/user-profile");
  };

  return (
    <DashboardWrapper showBtmHeader={false} isList={false}>
      <ProfileForm formHeader="Add User">
        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid container rowSpacing={{ md: 4, xs: 2.5 }} columnSpacing={2}>
            <Grid item xs={12} lg={5} md={6} sm={6}>
              <Box className={styles.profile_grid}>
                <Stack
                  flexDirection="row"
                  className={styles.profile_stack}
                  alignItems="center"
                >
                  <label>First Name</label>
                  <Box className={styles.form_input_group}>
                    <Controller
                      control={control}
                      name="firstName"
                      render={({ field: { onChange, value } }) => (
                        <CustomInput
                          type="text"
                          value={value}
                          onChange={onChange}
                          error={Boolean(errors?.firstName)}
                          helperText={errors?.firstName?.message}
                        />
                      )}
                    />
                  </Box>
                </Stack>
              </Box>
              <Box className={styles.profile_grid}>
                <Stack
                  flexDirection="row"
                  className={styles.profile_stack}
                  alignItems="center"
                >
                  <label>Last Name</label>
                  <Box className={styles.form_input_group}>
                    <Controller
                      control={control}
                      name="lastName"
                      render={({ field: { onChange, value } }) => (
                        <CustomInput
                          type="text"
                          value={value}
                          onChange={onChange}
                          error={Boolean(errors?.lastName)}
                          helperText={errors?.lastName?.message}
                        />
                      )}
                    />
                  </Box>
                </Stack>
              </Box>
              <Box className={`${styles.profile_grid} ${styles.mb_0}`}>
                <Stack
                  flexDirection="row"
                  className={styles.profile_stack}
                  alignItems="center"
                >
                  <label>Email</label>
                  <Box className={styles.form_input_group}>
                    <Controller
                      control={control}
                      name="email"
                      render={({ field: { onChange, value } }) => (
                        <CustomInput
                          type="email"
                          value={value}
                          onChange={onChange}
                          error={Boolean(errors?.email)}
                          helperText={errors?.email?.message}
                        />
                      )}
                    />
                  </Box>
                </Stack>
              </Box>
            </Grid>
            <Grid item xs={12} lg={5} md={6} sm={6}>
              <Box className={styles.profile_grid}>
                <Stack
                  flexDirection="row"
                  className={styles.profile_stack}
                  alignItems="center"
                >
                  <label>Admin User Level</label>
                  <Box className={styles.form_input_group}>
                    <Controller
                      control={control}
                      name="adminLevel"
                      render={({ field: { onChange, value } }) => (
                        <Box className={styles.selectWrap}>
                          <Select
                            displayEmpty
                            className={styles.cmn_slct}
                            defaultValue=""
                            id="demo-simple-select"
                            onChange={onChange}
                            // value={value}
                            value={value ? value : ""}
                          >
                            <MenuItem value="" disabled>
                              --Select level--
                            </MenuItem>
                            {administratorList?.map((e, index) => {
                              return (
                                <MenuItem key={e?._id} value={e?._id}>
                                  <Typography
                                    variant="body1"
                                    className="menu_para"
                                  >
                                    {e?.name}
                                  </Typography>
                                </MenuItem>
                              );
                            })}
                          </Select>
                        </Box>
                      )}
                    />
                    {errors.adminLevel && (
                      <p className={`${styles.error_text} ${styles.err_txt2}`}>
                        {validationText.error.adminLevel}
                      </p>
                    )}
                  </Box>
                </Stack>
              </Box>
              <Box className={styles.profile_grid}>
                <Stack
                  flexDirection="row"
                  className={styles.profile_stack}
                  alignItems="center"
                >
                  <label>Password</label>
                  <Box className={styles.form_input_group}>
                    <Controller
                      control={control}
                      name="password"
                      render={({ field: { onChange, value } }) => (
                        <CustomInput
                          type="password"
                          value={value}
                          onChange={onChange}
                          error={Boolean(errors?.password)}
                          helperText="enter at least 4 characters"
                        />
                      )}
                    />
                  </Box>
                </Stack>
              </Box>
              <Box className={`${styles.profile_grid} ${styles.mb_0}`}>
                <Stack
                  flexDirection="row"
                  className={styles.profile_stack}
                  alignItems="center"
                >
                  <label>Confirm Password</label>
                  <Box className={styles.form_input_group}>
                    <Controller
                      control={control}
                      name="confirmPassword"
                      render={({ field: { onChange, value } }) => (
                        <CustomInput
                          type="password"
                          value={value}
                          onChange={onChange}
                          error={Boolean(errors?.confirmPassword)}
                          helperText={errors?.confirmPassword?.message}
                        />
                      )}
                    />
                 {getValues("confirmPassword") && isNotMatchPassword &&  <p className={styles.error_text}>
                        {validationText.error.password_mismatch}
                      </p>}
                  </Box>
                </Stack>
              </Box>
            </Grid>
            <Grid item xs={12} lg={2} md={12} sm={12}>
              <Box className={styles.profile_btn}>
                <Box className={styles.black_btn}>
                  <CustomButton onClick={redirectToProfile}>
                    <Typography variant="caption">Close</Typography>
                  </CustomButton>
                </Box>

                  <CustomButton type="submit" disabled={isNotMatchPassword} loading={isLoading}>
                    <Typography variant="caption">{isLoading ? "Saving.." : "Save Profile"}</Typography>
                  </CustomButton>
              
              </Box>
            </Grid>
          </Grid>
        </form>
      </ProfileForm>
    </DashboardWrapper>
  );
}
